import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionType,
  Separator,
} from 'platform/components';
import {Box, HStack, Space, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {defaultTo, head, isNil, omit} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  MaterialDiscountType,
  PostCustomerContractMaterialDiscountApiArg,
  useGetCustomerContractMaterialDiscountQuery,
  usePatchCustomerContractMaterialDiscountMutation,
  usePostCustomerContractMaterialDiscountMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getDecimalFromPercentage, getPercentageFromDecimal, handleApiError} from '@dms/shared';

import {CurrencyCodeType, suffixTestId, TestIdProps} from 'shared/web';

import {DiscountAllowedVariant} from '../../../components/DiscountAllowedVariant';
import {VehicleFields} from '../../../components/VehicleFields';
import {ageTypeOptions} from '../../../constants/ageTypeOptions';
import {DiscountFormType, discountFormSchema} from '../../../types/DiscountFormType';

interface MaterialDiscountFormProps extends TestIdProps {
  materialDiscountId?: string;
  customerContractId?: string;
  onClose: VoidFunction;
  afterSubmit: VoidFunction;
}

export function MaterialDiscountForm(props: MaterialDiscountFormProps) {
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const {data: tenant} = useGetTenantQuery();
  const {data, isLoading, isError} = useGetCustomerContractMaterialDiscountQuery(
    {
      customerContractId: props.customerContractId ?? '',
      materialDiscountId: props.materialDiscountId ?? '',
    },
    {skip: isNil(props.materialDiscountId)}
  );

  const [postMaterialDiscount] = usePostCustomerContractMaterialDiscountMutation();
  const [patchMaterialDiscount] = usePatchCustomerContractMaterialDiscountMutation();

  const handleSubmit: FormSubmitHandler<DiscountFormType> = async (data) => {
    const body: PostCustomerContractMaterialDiscountApiArg['body'] = {
      ...omit(['vehicleType', 'vehicleMake'], data),
      vehicleTypes: data.vehicleType ? [data.vehicleType] : undefined,
      vehicleMakes: data.vehicleMake ? [data.vehicleMake] : undefined,
      directSaleVariantIds: data.isDirectSaleVariant ? data.directSaleVariantIds : undefined,
      serviceOrderVariantIds: data.isServiceOrderVariant ? data.serviceOrderVariantIds : undefined,
      sparePartManufacturers: data.sparePartManufacturers ?? undefined,
      discountSettingsValue: getDecimalFromPercentage(data.discountSettingsValue),
    };

    await (
      isNotNil(props.materialDiscountId)
        ? patchMaterialDiscount({
            customerContractId: props.customerContractId ?? '',
            materialDiscountId: props.materialDiscountId,
            body,
          })
        : postMaterialDiscount({
            customerContractId: props.customerContractId ?? '',
            body,
          })
    )
      .unwrap()
      .then(() => {
        props.onClose();
        props.afterSubmit();
      })
      .catch(handleApiError);
  };

  const defaultValues: Partial<DiscountFormType> = {
    ...data,
    name: data?.name ?? '',
    vehicleType: head(data?.vehicleTypes ?? []),
    vehicleMake: head(data?.vehicleMakes ?? []),
    vehicleAgeType: data?.vehicleAgeType ?? 'VEHICLE_AGE_UNDECIDED',
    isServiceOrderVariant: defaultTo(true)(data?.isServiceOrderVariant),
    isDirectSaleVariant: defaultTo(true)(data?.isDirectSaleVariant),
    sparePartManufacturers: data?.sparePartManufacturers ?? [],
    discountSettingsType: data?.discountSettingsType ?? 'DISCOUNT_CUSTOMER_GROUP',
    discountSettingsValue:
      isNotNil(data) && isNotNil(data?.discountSettingsValue)
        ? getPercentageFromDecimal(data.discountSettingsValue)
        : undefined,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={198}>
      <Form<DiscountFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={discountFormSchema}
        defaultValues={defaultValues}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="name"
              type="text"
              label={i18n.t('general.labels.name')}
              isRequired
              data-testid={suffixTestId('name', props)}
            />
            <Separator spacing={0} />
            <VehicleFields
              control={control}
              formApi={formApi}
              {...props}
              showSparePartManufacturers
            />
            <HStack spacing={4}>
              <Box flex={1}>
                <FormField
                  control={control}
                  name="vehicleAgeType"
                  type="radio"
                  options={ageTypeOptions}
                  label={i18n.t('entity.vehicle.labels.vehicleAge')}
                  onChange={() => {
                    formApi.setValue('vehicleAgeFrom', undefined);
                    formApi.setValue('vehicleAgeTo', undefined);
                  }}
                  data-testid={suffixTestId('vehicleAgeType', props)}
                />
              </Box>
              <Box flex={1}>
                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="vehicleAgeFrom"
                      type="integer"
                      label={i18n.t('entity.customerContract.labels.ageFrom')}
                      isDisabled={formApi.watch('vehicleAgeType') !== 'VEHICLE_AGE_IN_MONTHS'}
                      data-testid={suffixTestId('vehicleAgeFrom', props)}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      name="vehicleAgeTo"
                      type="integer"
                      label={i18n.t('entity.customerContract.labels.ageTo')}
                      isDisabled={formApi.watch('vehicleAgeType') !== 'VEHICLE_AGE_IN_MONTHS'}
                      data-testid={suffixTestId('vehicleAgeTo', props)}
                    />
                  </Box>
                </HStack>
              </Box>
            </HStack>
            <Separator spacing={0} />
            <DiscountAllowedVariant
              control={control}
              data-testid={suffixTestId('discountAllowedVariant', props)}
            />
            <Card
              variant="inlineGrey"
              title={i18n.t('entity.workshopCustomerGroup.labels.discountSettings')}
            >
              <HStack spacing={4}>
                <Box flex={1}>
                  <FormField
                    control={control}
                    name="discountSettingsType"
                    type="radio"
                    options={getDiscountOptions(
                      formatCurrencySymbol(tenant?.currency as CurrencyCodeType)
                    )}
                    direction="column"
                    spacing={4}
                    data-testid={suffixTestId('discountSettingsType', props)}
                  />
                  <Space vertical={4} />
                  <FormField
                    control={control}
                    name="discountSettingsValue"
                    type="number"
                    isStepperVisible
                    minStepperValue={0}
                    maxStepperValue={
                      formApi.watch('discountSettingsType') === 'PERCENTAGE_DISCOUNT'
                        ? 100
                        : undefined
                    }
                    decimalPlaces={2}
                    helperText={i18n.t('entity.customerContract.labels.discountDescription')}
                    data-testid={suffixTestId('discountSettingsValue', props)}
                  />
                </Box>
                <Space fillAvailable />
              </HStack>
            </Card>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={props.onClose}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={
                  props.materialDiscountId
                    ? i18n.t('general.actions.saveChanges')
                    : i18n.t('general.actions.create')
                }
                data-testid={suffixTestId('submit', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const getDiscountOptions = (currency: string): OptionType<MaterialDiscountType>[] => [
  {
    label: i18n.t('entity.customerContract.labels.discountByCustomerGroup', {currency}),
    value: 'DISCOUNT_CUSTOMER_GROUP',
  },
  {
    label: i18n.t('entity.customerContract.labels.discountOnSalePrice'),
    value: 'DISCOUNT_SALE_PRICE',
  },
  {
    label: i18n.t('entity.customerContract.labels.marginToPurchasePrice'),
    value: 'DISCOUNT_MARGIN_PURCHASE_PRICE',
  },
];
